import { Flex, Grid, Box, Heading, Text, Container, useBreakpointValue } from '@chakra-ui/react'
import { sanitize } from 'isomorphic-dompurify'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'

import { ProjectsAPI } from '@miimosa/api'
import { v3toFrontSearchProject, extractString } from '@miimosa/common'
import type { FrontSearchProjectType, CountryCode, Lang } from '@miimosa/common/types'
import { Pagination } from '@miimosa/design-system/components/ClientSidePagination'
import { parseLocale } from '@miimosa/design-system/lib/hooks/use_platform'
import getPageTitle from '@miimosa/design-system/lib/i18n/helpers/getPageTitle'
import { directusAsset, type Partner, partner } from '@miimosa/directus'
import { Search } from '@miimosa/protocol'

import { ProjectCard } from '@components'

const getPartnerFromDirectus = async (slug: string): Promise<Partner[]> => {
  try {
    const {
      data: { data },
    } = await partner({ slug })
    return data
  } catch (error) {
    return []
  }
}

const fetchProjects = async (
  query: Record<string, string | string[] | undefined>,
  platform: CountryCode,
  lang: Lang,
  partnerSlug: string
): Promise<{
  projects: Search.SearchProject[]
  totalProjects: number
  perPage: number
  currentPage: number
}> => {
  const pageAsString = extractString(query.page)
  const PER_PAGE = 6
  const currentPage = Number(pageAsString || 1)

  const projectsAPI = new ProjectsAPI()

  try {
    const results = await projectsAPI.getProjectsFromSearch({
      query: '',
      type_filter: 'PROJECT_TYPE_UNSPECIFIED',
      categories_filter: [],
      around_me_filter: undefined,
      pagination: undefined,
      state_filter: [],
      regional_code_filter: [],
      preset: 'PRESET_UNSPECIFIED',
      legacy_pagination: {
        items_per_page: PER_PAGE,
        page: currentPage,
      },
      campaign_filter: [],
      labels_filter: [],
      branch_code: `mii_${platform}`,
      locale: lang,
      collect_type_filter: 'COLLECT_TYPE_UNSPECIFIED',
      partner_filter: [partnerSlug],
      collect_goal_type_filter: ['COLLECT_GOAL_TYPE_AMOUNT', 'COLLECT_GOAL_TYPE_QUANTITY'],
      slug_filter: [],
    })

    return {
      projects: results.kind === 'success' ? results.data.projects : [],
      totalProjects: results.kind === 'success' ? results.data.total_projects : 0,
      perPage: PER_PAGE,
      currentPage,
    }
  } catch (err) {
    return { projects: [], totalProjects: 0, perPage: 6, currentPage: 0 }
  }
}

export const getServerSideProps: GetServerSideProps = async ({ query, locale, params }) => {
  const { platform, lang } = parseLocale(locale)
  const slug = extractString(params?.slug)
  if (!slug) {
    return {
      notFound: true,
    }
  }
  const partners = await getPartnerFromDirectus(slug)

  if (!partners || partners.length < 1) {
    return {
      notFound: true,
    }
  }

  const projectsResults = await fetchProjects(query, platform, lang, partners[0].slug)
  let projects: FrontSearchProjectType[] = []
  if (projectsResults) {
    projects = projectsResults.projects.map((project) => v3toFrontSearchProject({ ...project, imageFileSize: 'card' }))
  }
  return {
    props: {
      ...(await serverSideTranslations(lang, ['common', 'partner'])),
      partner: partners.length ? partners[0] : {},
      projects: projects,
      perPage: projectsResults.perPage,
      totalProjects: projectsResults.totalProjects,
      currentPage: projectsResults.currentPage,
    },
  }
}

interface Props {
  partner: Partner
  projects: FrontSearchProjectType[]
  perPage: number
  currentPage: number
  totalProjects: number
}

const Partner: FC<Props> = ({ partner, projects, perPage, currentPage, totalProjects }) => {
  const { query } = useRouter()
  const { slug } = query
  const { t } = useTranslation('partner')
  const { name, image, translations } = partner || {}
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'md' })

  const [clientCurrentPage, setClientCurrentPage] = useState(currentPage)

  //Needed because client current page state not updated when you push to new route...
  useEffect(() => {
    setClientCurrentPage(currentPage)
  }, [currentPage])

  return (
    <>
      <Head>
        <title>{`${getPageTitle(t)} ${slug}`}</title>
        <meta property="og:title" content={`${getPageTitle(t)} ${slug}`} key="title" />
      </Head>
      <Container variant="full" bg="creamy">
        <Container variant="boxed" flexDirection="column" alignItems="flex-start" px={20}>
          <Flex mb="8" alignItems="center">
            {image && (
              <Box
                mx="6"
                bgImage={directusAsset(image)}
                width="170px"
                height="170px"
                bgSize="contain"
                bgPosition="center"
                bgRepeat="no-repeat"
              />
            )}
            <Flex direction="column" ml="10">
              <Heading size="lg" as="h1">
                {name}
              </Heading>
              <Text>{translations && translations[0].short_description}</Text>
            </Flex>
          </Flex>
          {translations && (
            <Box
              className="ck ck-content __description project-tabs-content"
              dangerouslySetInnerHTML={{ __html: sanitize(translations[0].description) }}
            />
          )}
          {!!projects?.length && (
            <Heading size="md" mx="8" textAlign="center" mt="20" width="100%">
              {t('supported_projects')}
            </Heading>
          )}
          <Flex justifyContent="center" w="full">
            <Grid templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6} pb="10" mt="6">
              {projects?.map((project, i) => (
                <ProjectCard
                  key={`project_${i}`}
                  imageFileName={project.imageFileName}
                  interestsRatio={project.collect?.interestsRatio}
                  slug={project.slug}
                  title={project.title}
                  teasing={project.teasing}
                  goalAmount={project.collect?.goalAmount || 0}
                  place={project.place}
                  goalType={project.collect?.goalType || 'amount'}
                  collectType={project.collect?.type || 'donation'}
                  collectedAmount={project.collect?.collectedAmount || 0}
                  progress={project.collect?.progress || 0}
                  state={project.state}
                  endsAt={project.collect?.endsAt}
                  goalQuantity={project.collect?.goalQuantity || 0}
                  collectedQuantity={project.collect?.collectedQuantity || 0}
                  userFullName={project.userFullName}
                  avatarFileName={project.avatarFileName}
                  labels={project.labels}
                />
              ))}
            </Grid>
          </Flex>
          <Flex mt="4" alignItems="center" width="100%" justify="center">
            <Pagination
              currentPage={clientCurrentPage}
              totalPageCount={Math.ceil(totalProjects / perPage)}
              siblingCount={2}
              showArrow={isMobile ? false : true}
            />
          </Flex>
        </Container>
      </Container>
    </>
  )
}

export default Partner
